import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './TextPanelManagementEditor.module.scss';
import Spinner from '../../../../../../../common/components/Spinner';
import VideoService from '../../../../../../service/VideoService';
import { CustomButton } from '../../../../../../components/Button/Button';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../../../components/Icon/Icon';
import Slider from 'rc-slider';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import SelectOption from '../../../../../../components/SelectOption/SelectOption';
import useObjectSimulation from '../../../../../../hooks/useObjectSimulation';
import { useHistory } from 'react-router-dom';
import { checkFontLoaded } from '../../../../../../utils/SimulatedItemsUtility';
import cx from 'classnames';
import {
  EXAMPLE_BOTTOM_TEXT,
  EXAMPLE_TOP_TEXT,
  PANEL_TYPE,
  PREVIEW_IDS,
  SPACE_BETWEEN_PANELS_MIN_VALUE,
  SPACE_BETWEEN_PANELS_MAX_VALUE,
  OPACITY_MIN_VALUE,
  OPACITY_MAX_VALUE,
  MARGIN_MIN_VALUE,
  MARGIN_MAX_VALUE,
  FONT_SIZE_MIN,
  FONT_SIZE_MAX,
  BORDER_RADIUS_MIN,
  BORDER_RADIUS_MAX,
  BORDER_WIDTH_MIN,
  BORDER_WIDTH_MAX,
  PADDING_MAX,
  INITIAL_TEXT_PANEL_SETTINGS,
  PANEL_NAME,
} from '../../constants';
import { AVAILABLE_POSITIONS } from '../../../../../../../common/constants/common';
import ColorPicker from '../../../../../../components/ColorPicker/ColorPicker';
import { RGBAToHexA } from '../../../../../../utils/CallToActionHelper';
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from '../../../../../../context/UserContext';
import { CustomButtonTooltip } from '../../../../../../components/ButtonTooltip/ButtonTooltip';
import VormatsAPI from '../../../../../../../common/apis/VormatsAPI';
import { useNodeRef } from 'engage/hooks/useNodeRef';

const trackStyle = {
  background: 'none',
  height: '4px',
  borderRadius: '2px',
  position: 'relative',
  top: '-4px',
};

const railStyle = {
  background: '#EEEEEE',
  height: '4px',
  borderRadius: '2px',
  position: 'relative',
};

const dotStyle = {
  marginLeft: '2px',
  bottom: '4px',
  width: '12px',
  height: '12px',
  border: 'none',
  background: '#EEEEEE',
};

const handleStyle = {
  width: '18px',
  height: '18px',
  background: '#FFFFFF',
  position: 'relative',
  top: '-10px',
  borderRadius: '9px',
  boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
  cursor: 'pointer',
};

const TextPanelManagementEditor = ({ textPanelId, onClose, companyId }) => {
  const [currentTextPanel, setCurrentTextPanel] = useState({});
  const [initialTextPanel, setInitialTextPanel] = useState({});

  const [landscapeTextPanelStyles, setLandscapeTextPanelStyles] = useState({});
  const [portraitTextPanelStyles, setPortraitTextPanelStyles] = useState({});
  const [squareTextPanelStyles, setSquareTextPanelStyles] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [isChanged, setIsChanged] = useState(false);
  const [fonts, setFonts] = useState();

  const previewRef = useRef(null);
  const infoRef = useRef(null);

  const { t } = useTranslation();

  const history = useHistory();

  const { appConfig, fonts: appConfigFonts, updateUserAppConfig } = useUserContext();

  const noChosenFont = useMemo(() => isChanged && (!currentTextPanel?.top?.font || !currentTextPanel?.bottom?.font), [
    isChanged,
    currentTextPanel,
  ]);

  const isOnePanelType = useMemo(() => currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type, [currentTextPanel.type]);

  const availableFonts = useMemo(() => {
    if (!fonts) {
      return [];
    }
    let fontsMap = new Map();
    let uniqueFonts = [
      ...appConfig?.fonts?.map(item => {
        const newFont = {};
        for (let key in item) {
          newFont[key.replace('font_', '')] = item[key];
        }
        return newFont;
      }),
      ...fonts,
    ].filter(item => {
      const font = fontsMap.get(item.name);
      if (font) {
        if (item.id < font) {
          fontsMap.delete(item.name);
          fontsMap.set(item.name, item.id);
          return true;
        } else {
          return false;
        }
      }
      fontsMap.set(item.name, item.id);
      return true;
    });

    return uniqueFonts;
  }, [appConfig, fonts]);

  const { nodeRef: landscapePreviewNode, handleNodeRef: handleLandscapeNodeRef } = useNodeRef();

  const {
    objectPosition: landscapePosition,
    recalculateVuToPx: landscapeRecalculateVuToPx,
    loading: landscapeLoading,
  } = useObjectSimulation({
    parentNode: landscapePreviewNode,
    id: PREVIEW_IDS.landscape,
    mode: 'textPanel',
    margin_x: currentTextPanel?.marginX,
    margin_y: currentTextPanel?.marginY,
    position: currentTextPanel?.position,
    isLandscapePreview: true,
  });

  const { nodeRef: portraitPreviewNode, handleNodeRef: handlePortraitNodeRef } = useNodeRef();

  const {
    objectPosition: portraitPosition,
    recalculateVuToPx: portraitRecalculateVuToPx,
    loading: portraitLoading,
  } = useObjectSimulation({
    parentNode: portraitPreviewNode,
    id: PREVIEW_IDS.portrait,
    mode: 'textPanel',
    margin_x: currentTextPanel?.marginX,
    margin_y: currentTextPanel?.marginY,
    position: currentTextPanel?.position,
    isPortraitPreview: true,
  });

  const { nodeRef: squarePreviewNode, handleNodeRef: handleSquareNodeRef } = useNodeRef();

  const {
    objectPosition: squarePosition,
    recalculateVuToPx: squareRecalculateVuToPx,
    loading: squareLoading,
  } = useObjectSimulation({
    parentNode: squarePreviewNode,
    id: PREVIEW_IDS.square,
    mode: 'textPanel',
    margin_x: currentTextPanel?.marginX,
    margin_y: currentTextPanel?.marginY,
    position: currentTextPanel?.position,
    isSquarePreview: true,
  });

  const convertColorIfNeeded = color => {
    if (color) {
      return RGBAToHexA(color);
    }

    if (color === null) {
      return '#FFFFFF';
    }

    return color;
  };

  const recalculatePaddingWithPx = (paddingString, callback) => {
    return paddingString
      ?.split(' ')
      .map(item => `${callback(Number(item))}px`)
      .join(' ');
  };

  const getStylesForOrientationsPreview = async (recalculateCallback, setCallback) => {
    const parseLabelStyle = async (labelObject, type) => {
      if (!labelObject) {
        return {};
      }

      try {
        await checkFontLoaded(labelObject.font);
      } catch (e) {
        console.log('Font loaded failed with error:', e);
      }

      return {
        borderStyle: 'solid',
        borderWidth: recalculateCallback(labelObject.borderWidth),
        borderColor: labelObject.borderColor,
        borderRadius: recalculateCallback(labelObject.borderRadius),
        opacity: labelObject.opacity + '%',
        color: labelObject.fontColor,
        fontFamily: `'${labelObject.font?.name}'`,
        fontSize: recalculateCallback(labelObject.fontSize),
        ...(type === PANEL_TYPE.TWO_PANELS.type && {
          padding: recalculatePaddingWithPx(labelObject.textPadding, recalculateCallback),
        }),
        ...(type === PANEL_TYPE.TWO_PANELS.type && { backgroundColor: labelObject.backgroundColor }),
      };
    };

    const topLabel = currentTextPanel.top;
    const bottomLabel = currentTextPanel.bottom;

    if (currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type) {
      bottomLabel.borderWidth = 0;
      bottomLabel.borderRadius = 0;
      bottomLabel.borderColor = '';
    }

    const topLabelStyles = await parseLabelStyle(topLabel, currentTextPanel.type);
    const bottomLabelStyles = await parseLabelStyle(bottomLabel, currentTextPanel.type);

    const containerStyles = {};

    if (isOnePanelType) {
      containerStyles.padding = recalculatePaddingWithPx(currentTextPanel.textPadding, recalculateCallback);
      containerStyles.backgroundColor = currentTextPanel.bgColor;
      containerStyles.borderWidth = topLabelStyles.borderWidth;
      containerStyles.borderRadius = topLabelStyles.borderRadius;
      containerStyles.borderColor = topLabelStyles.borderColor;
      containerStyles.borderStyle = topLabelStyles.borderStyle;
      containerStyles.opacity = topLabelStyles.opacity;
      topLabelStyles.opacity = 100;

      topLabelStyles.borderWidth = 0;
      delete topLabelStyles.borderRadius;
      delete topLabelStyles.borderColor;
    } else {
      bottomLabelStyles.opacity = topLabelStyles.opacity;
    }

    topLabelStyles.marginBottom = recalculateCallback(currentTextPanel.spacing);

    setCallback({
      topLabelStyles,
      bottomLabelStyles,
      containerStyles,
    });
  };

  const generateNewTemplate = useCallback(async () => {
    setIsLoading(true);

    const newTextPanel = { ...INITIAL_TEXT_PANEL_SETTINGS, id: uuidv4() };
    newTextPanel.top.font = availableFonts[0];
    newTextPanel.bottom.font = availableFonts[0];

    setCurrentTextPanel(newTextPanel);
    setInitialTextPanel(newTextPanel);
    setIsChanged(true);

    setIsLoading(false);
  }, [uuidv4, availableFonts]);

  const getTextPanel = async () => {
    try {
      setIsLoading(true);
      const result = parseTextPanelObject(await VideoService.getTextPanel(textPanelId, companyId));
      setCurrentTextPanel(result);
      setInitialTextPanel(result);
    } catch (err) {
      console.log(err);
      onClose();
    }

    setIsLoading(false);
  };

  const handleUpdateValues = (key, value, panelName = null) => {
    const number = Number(value);
    const isNumber = Number.isInteger(number);

    if (key === 'marginX' || key === 'marginY') {
      if (value === '') {
        handleUpdateCurrentTextPanel({ [key]: MARGIN_MIN_VALUE });
        return;
      }

      if (!isNumber || number > MARGIN_MAX_VALUE) {
        return;
      }
    }

    if (key === 'opacity') {
      if (value === '') {
        handleUpdatePanelObject(panelName, { [key]: OPACITY_MIN_VALUE - 1 });
        return;
      }

      if (!isNumber || number < OPACITY_MIN_VALUE || number > OPACITY_MAX_VALUE) {
        return;
      }

      handleUpdatePanelObject(panelName, { [key]: number });
      return;
    }

    if (key === 'spacing') {
      if (value === '') {
        handleUpdateCurrentTextPanel({ [key]: SPACE_BETWEEN_PANELS_MIN_VALUE });
        return;
      }

      if (!isNumber || number > SPACE_BETWEEN_PANELS_MAX_VALUE) {
        return;
      }
    }

    if (key === 'font') {
      handleUpdatePanelObject(panelName, { [key]: value });
      return;
    }

    if (key === 'fontSize') {
      if (value === '') {
        handleUpdatePanelObject(panelName, { [key]: '' });
        return;
      }

      if (!isNumber) {
        return;
      }

      if (number < FONT_SIZE_MIN) {
        handleUpdatePanelObject(panelName, { [key]: FONT_SIZE_MIN });
        return;
      }

      if (number > FONT_SIZE_MAX) {
        handleUpdatePanelObject(panelName, { [key]: FONT_SIZE_MAX });
        return;
      }

      handleUpdatePanelObject(panelName, { [key]: number });
      return;
    }

    if (key === 'borderRadius') {
      if (value === '') {
        handleUpdatePanelObject(panelName, { [key]: BORDER_RADIUS_MIN });
        return;
      }

      if (!isNumber || number > BORDER_RADIUS_MAX) {
        return;
      }

      handleUpdatePanelObject(panelName, { [key]: number });
      return;
    }

    if (key === 'borderWidth') {
      if (value === '') {
        handleUpdatePanelObject(panelName, { [key]: BORDER_WIDTH_MIN });
        return;
      }

      if (!isNumber || number > BORDER_WIDTH_MAX) {
        return;
      }

      handleUpdatePanelObject(panelName, { [key]: number });
      return;
    }

    if (key === 'fontColor' || key === 'borderColor') {
      handleUpdatePanelObject(panelName, { [key]: value });
      return;
    }

    if (key === 'backgroundColor') {
      if (isOnePanelType) {
        handleUpdateCurrentTextPanel({ bgColor: value });
        return;
      }

      handleUpdatePanelObject(panelName, { [key]: value });
      return;
    }

    if (key === 'paddingLeft' || key === 'paddingRight' || key === 'paddingTop' || key === 'paddingBottom') {
      if (!isNumber || number > PADDING_MAX) {
        return;
      }

      if (isOnePanelType) {
        if (value === '') {
          const [top, right, bottom, left] = currentTextPanel?.textPadding?.split(' ') || [];
          const newValue = [
            key === 'paddingTop' ? 0 : top,
            key === 'paddingRight' ? 0 : right,
            key === 'paddingBottom' ? 0 : bottom,
            key === 'paddingLeft' ? 0 : left,
          ];

          handleUpdateCurrentTextPanel({ textPadding: newValue.join(' ') });
          return;
        }

        const [top, right, bottom, left] = currentTextPanel?.textPadding?.split(' ') || [];
        const newValue = [
          key === 'paddingTop' ? number : top,
          key === 'paddingRight' ? number : right,
          key === 'paddingBottom' ? number : bottom,
          key === 'paddingLeft' ? number : left,
        ];

        handleUpdateCurrentTextPanel({ textPadding: newValue.join(' ') });
        return;
      }

      if (value === '') {
        const [top, right, bottom, left] = currentTextPanel[panelName]?.textPadding?.split(' ') || [];
        const newValue = [
          key === 'paddingTop' ? 0 : top,
          key === 'paddingRight' ? 0 : right,
          key === 'paddingBottom' ? 0 : bottom,
          key === 'paddingLeft' ? 0 : left,
        ];

        handleUpdatePanelObject(panelName, { textPadding: newValue.join(' ') });
        return;
      }

      const [top, right, bottom, left] = currentTextPanel[panelName]?.textPadding?.split(' ') || [];
      const newValue = [
        key === 'paddingTop' ? number : top,
        key === 'paddingRight' ? number : right,
        key === 'paddingBottom' ? number : bottom,
        key === 'paddingLeft' ? number : left,
      ];

      handleUpdatePanelObject(panelName, { textPadding: newValue.join(' ') });
      return;
    }

    handleUpdateCurrentTextPanel({ [key]: number });
  };

  const handleUpdateCurrentTextPanel = useCallback(changes => {
    let typeChanged = changes.hasOwnProperty('type') && changes.type !== currentTextPanel.type;
    setCurrentTextPanel(prevState => ({
      ...prevState,
      ...(typeChanged &&
        (changes.type === PANEL_TYPE.TWO_PANELS.type
          ? {
          top: { ...prevState.top, backgroundColor: prevState.bgColor == prevState.top.fontColor ? 'rgb(255, 255, 255)' : prevState.bgColor, textPadding: prevState.textPadding },
          bottom: { ...prevState.bottom, backgroundColor: prevState.bgColor == prevState.bottom.fontColor ? 'rgb(255, 255, 255)' : prevState.bgColor, textPadding: prevState.textPadding },
            }
          : {
              bgColor: prevState.top.backgroundColor,
              textPadding: prevState.top.textPadding,
              bottom: { ...prevState.bottom, borderRadius: 0, borderWidth: 0, borderColor: '#FFFFFF' },
            })),
      ...changes,
    }));
    setIsChanged(true);
  }, [currentTextPanel]);

  const handleUpdatePanelObject = (panelName, changes) => {
    const originalPanel = currentTextPanel[panelName];
    handleUpdateCurrentTextPanel({ [panelName]: { ...originalPanel, ...changes } });
  };

  const handleCancel = () => {
    setCurrentTextPanel(initialTextPanel);
    setIsChanged(false);
  };

  const parseTextPanelObject = textPanel => {
    const { id, bgColor, spacing, marginX, marginY, position, textPadding, title, type } = textPanel;

    return {
      id,
      bgColor,
      spacing,
      marginX,
      marginY,
      position,
      textPadding,
      title,
      type,
      ...(type === PANEL_TYPE.ONE_PANEL.type
        ? { top: textPanel.labelTop, bottom: textPanel.labelBottom }
        : { top: textPanel.panelTop, bottom: textPanel.panelBottom }),
    };
  };

  const isCenterPosition = currentTextPanel?.position === 'tc' || currentTextPanel?.position === 'bc';

  const handleSave = async () => {
    setIsLoading(true);

    if (textPanelId === 'new') {
      try {
        const { top, bottom, bgColor, spacing, marginX, marginY, position, textPadding, title, type } = currentTextPanel;

        top.fontId = top.font.id;
        bottom.fontId = bottom.font.id;

        if (type === PANEL_TYPE.ONE_PANEL.type) {
          top.backgroundColor = bgColor;
          top.textPadding = textPadding;
        }

        // update code here
        const payload = {
          title,
          type,
          position,
          marginX,
          marginY,
          spacing,
          panelTop: top,
          panelBottom: bottom,
          default: true,
        };

        const createdTextPanel = parseTextPanelObject(await VideoService.createTextPanel(payload, companyId));

        setCurrentTextPanel(createdTextPanel);
        setInitialTextPanel(createdTextPanel);

        setIsChanged(false);
        ToastNotification({
          message: t('company.settings.text_panels.create.success'),
          iconName: 'success',
        });
      } catch (err) {
        console.log(err);
        ToastNotification({ message: t('company.settings.text_panels.create.error') });
      }
    } else {
      try {
        const { top, bottom, bgColor, spacing, marginX, marginY, position, textPadding, title, type } = currentTextPanel;

        if (top?.font) {
          top.fontId = top.font.id;
        }

        if (bottom?.font) {
          bottom.fontId = bottom.font.id;
        }

        if (type === PANEL_TYPE.ONE_PANEL.type) {
          top.backgroundColor = bgColor;
          top.textPadding = textPadding;
        }

        const payload = {
          panelTop: top,
          panelBottom: bottom,
          spacing,
          marginX,
          marginY,
          position,
          title,
          type,
        };

        const updatedTextPanel = parseTextPanelObject(await VideoService.updateTextPanel(textPanelId, payload, companyId));
        setCurrentTextPanel(updatedTextPanel);
        setInitialTextPanel(updatedTextPanel);

        setIsChanged(false);
        ToastNotification({
          message: t('company.settings.text_panels.update.success'),
          iconName: 'success',
        });
      } catch (err) {
        console.log(err);
        ToastNotification({ message: t('company.settings.text_panels.update.error') });
      }
    }

    updateUserAppConfig();
    onClose();
    setIsLoading(false);
  };

  useEffect(() => {
    if (textPanelId) {
      if (textPanelId === 'new') {
        generateNewTemplate();
      } else {
        getTextPanel();
      }
    }

    const checkscroll = () => {
      const { top } = infoRef.current?.getBoundingClientRect() || {};

      // Force the preview to move in the visible area
      if (previewRef?.current?.style) {
        previewRef.current.style.marginTop = (top < 0 ? Math.abs(top) : 0) + 'px';
      }
    };

    document.addEventListener('scroll', checkscroll);

    return () => document.removeEventListener('scroll', checkscroll);
  }, []);

  useEffect(() => {
    let unblock = isChanged
      ? history.block(() => {
          // Navigation was blocked! Let's show a confirmation dialog
          // so the user can decide if they actually want to navigate
          // away and discard changes they've made in the current page.
          if (confirm(t('clip_organizer.confirm_leave_page'))) {
            // Unblock the navigation.
            return true;
          } else {
            return false;
          }
        })
      : history.block(() => true);

    return () => unblock();
  }, [isChanged]);

  useEffect(() => {
    if (currentTextPanel?.id) {
      getStylesForOrientationsPreview(landscapeRecalculateVuToPx, setLandscapeTextPanelStyles);
    }
  }, [currentTextPanel, landscapeRecalculateVuToPx]);

  useEffect(() => {
    if (currentTextPanel?.id) {
      getStylesForOrientationsPreview(portraitRecalculateVuToPx, setPortraitTextPanelStyles);
    }
  }, [currentTextPanel, portraitRecalculateVuToPx]);

  useEffect(() => {
    if (currentTextPanel?.id) {
      getStylesForOrientationsPreview(squareRecalculateVuToPx, setSquareTextPanelStyles);
    }
  }, [currentTextPanel, squareRecalculateVuToPx]);

  useEffect(() => {
    const getCompanyFonts = async companyId => {
      const fonts = await VormatsAPI.getFontsList(companyId);
      setFonts(fonts);
    };
    if (!fonts) {
      if (companyId) {
        getCompanyFonts(companyId);
      } else {
        setFonts(appConfigFonts);
      }
    }
  }, [companyId, appConfigFonts]);

  return (
    <div className={styles.mainContainer}>
      {isLoading ? (
        <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
      ) : (
        <>
          <div ref={infoRef} className={styles.infoContainer}>
            <div className={styles.backContainer}>
              <CustomButton onClick={onClose} variant="link" className={styles.backButton}>
                <Icon src="arrowLeftStoryboard" className={styles.leftArrowIcon} />
                <span className={styles.btnLabel}>{t('general.back')}</span>
              </CustomButton>
            </div>
            <span className={styles.textPanelTitle}>{t('company.settings.text_panels.editor.title')}</span>
            <div className={styles.generalSection}>
              <span className={styles.generalTitle}>{t('company.settings.text_panels.editor.general')}</span>
              <div className={styles.generalValuesContainer}>
                <div className={styles.titleContainer}>
                  <div className={styles.titleInputContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.general.title')}</span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={currentTextPanel?.title}
                      onChange={event => handleUpdateCurrentTextPanel({ title: event.target?.value })}
                      maxLength={100}
                    />
                  </div>
                  <div className={styles.positionSelectContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.position')}</span>
                    <SelectOption
                      currentlySelected={AVAILABLE_POSITIONS.find(({ type }) => type === currentTextPanel?.position)}
                      options={AVAILABLE_POSITIONS}
                      onSelect={({ type }) => handleUpdateCurrentTextPanel({ position: type })}
                      currentInputClassName={styles.currentSelect}
                    />
                  </div>
                </div>
                <div className={styles.marginContainer}>
                  {!isCenterPosition && (
                    <div className={styles.marginAxisContainer}>
                      <div className={styles.marginAxisInputContainer}>
                        <span className={styles.inputText}>
                          {t('company.settings.text_panels.editor.marginX')}, <span>%</span>
                        </span>
                        <input
                          type="text"
                          className={cx(styles.inputField, styles.largeInputField)}
                          value={currentTextPanel?.marginX}
                          onChange={event => handleUpdateValues('marginX', event.target?.value)}
                        />
                      </div>
                      <Slider
                        min={MARGIN_MIN_VALUE}
                        max={MARGIN_MAX_VALUE}
                        step={1}
                        onChange={value => handleUpdateCurrentTextPanel({ marginX: value })}
                        value={currentTextPanel?.marginX}
                        included
                        style={{ left: '-6px', width: '50%' }}
                        handleStyle={handleStyle}
                        trackStyle={trackStyle}
                        railStyle={railStyle}
                        marks={{
                          [MARGIN_MIN_VALUE]: ' ',
                          [MARGIN_MAX_VALUE]: ' ',
                        }}
                        dotStyle={dotStyle}
                      />
                    </div>
                  )}
                  <div className={styles.marginAxisContainer}>
                    <div className={styles.marginAxisInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.text_panels.editor.marginY')}, <span>%</span>
                      </span>
                      <input
                        type="text"
                        className={cx(styles.inputField, styles.largeInputField)}
                        value={currentTextPanel?.marginY}
                        onChange={event => handleUpdateValues('marginY', event.target?.value)}
                      />
                    </div>
                    <Slider
                      min={MARGIN_MIN_VALUE}
                      max={MARGIN_MAX_VALUE}
                      step={1}
                      onChange={value => handleUpdateCurrentTextPanel({ marginY: value })}
                      value={currentTextPanel?.marginY}
                      included
                      style={{ left: '-6px', width: '50%' }}
                      handleStyle={handleStyle}
                      trackStyle={trackStyle}
                      railStyle={railStyle}
                      marks={{
                        [MARGIN_MIN_VALUE]: ' ',
                        [MARGIN_MAX_VALUE]: ' ',
                      }}
                      dotStyle={dotStyle}
                    />
                  </div>
                </div>
                <div className={styles.typeSelectContainer}>
                  <span className={styles.inputText}>{t('company.settings.text_panels.editor.type')}</span>
                  <SelectOption
                    currentlySelected={Object.values(PANEL_TYPE).find(({ type }) => type === currentTextPanel?.type)}
                    options={Object.values(PANEL_TYPE)}
                    onSelect={({ type }) => handleUpdateCurrentTextPanel({ type })}
                    currentInputClassName={styles.currentSelect}
                  />
                </div>
                <div className={styles.spaceContainer}>
                  <div className={styles.spaceInputContainer}>
                    <span className={styles.inputText}>
                      {t('company.settings.text_panels.editor.spacing')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={styles.inputField}
                      value={currentTextPanel?.spacing}
                      onChange={event => handleUpdateValues('spacing', event.target?.value)}
                    />
                  </div>
                  <Slider
                    min={SPACE_BETWEEN_PANELS_MIN_VALUE}
                    max={SPACE_BETWEEN_PANELS_MAX_VALUE}
                    step={1}
                    onChange={value => handleUpdateCurrentTextPanel({ spacing: value })}
                    value={currentTextPanel?.spacing}
                    included
                    style={{ left: '-6px' }}
                    handleStyle={handleStyle}
                    trackStyle={trackStyle}
                    railStyle={railStyle}
                    marks={{
                      [SPACE_BETWEEN_PANELS_MIN_VALUE]: ' ',
                      [SPACE_BETWEEN_PANELS_MAX_VALUE]: ' ',
                    }}
                    dotStyle={dotStyle}
                  />
                </div>
                <div className={styles.opacityContainer}>
                  <div className={styles.opacityInputContainer}>
                    <span className={styles.inputText}>
                      {t('company.settings.text_panels.editor.opacity')}, <span>%</span>
                    </span>
                    <input
                      type="text"
                      className={styles.inputField}
                      value={currentTextPanel?.top?.opacity}
                      onChange={event => handleUpdateValues('opacity', event.target?.value, PANEL_NAME.TOP)}
                    />
                  </div>
                  <Slider
                    min={OPACITY_MIN_VALUE}
                    max={OPACITY_MAX_VALUE}
                    step={1}
                    onChange={value => handleUpdatePanelObject('top', { opacity: value })}
                    value={currentTextPanel?.top?.opacity}
                    included
                    style={{ left: '-6px' }}
                    handleStyle={handleStyle}
                    trackStyle={trackStyle}
                    railStyle={railStyle}
                    marks={{
                      [OPACITY_MIN_VALUE]: ' ',
                      [OPACITY_MAX_VALUE]: ' ',
                    }}
                    dotStyle={dotStyle}
                  />
                </div>
              </div>
            </div>
            <div className={styles.panelSection}>
              <span className={styles.panelTitle}>{t('company.settings.text_panels.editor.top_panel')}</span>
              <div className={styles.panelValuesContainer}>
                <div className={styles.fontSizeContainer}>
                  <div className={styles.fontSelectContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font')}</span>
                    <SelectOption
                      currentlySelected={availableFonts.find(({ id }) => id === currentTextPanel?.top?.font?.id)}
                      options={availableFonts}
                      onSelect={value => handleUpdateValues('font', value, PANEL_NAME.TOP)}
                      currentInputClassName={styles.currentSelect}
                    />
                  </div>
                  <div className={styles.fontSizeInputContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font_size')}</span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={currentTextPanel?.top?.fontSize}
                      onChange={event => handleUpdateValues('fontSize', event.target?.value, PANEL_NAME.TOP)}
                      onBlur={() => currentTextPanel?.top?.fontSize === '' && handleUpdateValues('fontSize', 1, PANEL_NAME.TOP)}
                    />
                  </div>
                </div>
                <div className={styles.fontColorContainer}>
                  <div className={styles.fontColorPickerContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(currentTextPanel?.top?.fontColor)}
                      onChoose={value => handleUpdateValues('fontColor', value, PANEL_NAME.TOP)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                    />
                  </div>
                  <div className={styles.fontColorPickerContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.background_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(
                        isOnePanelType ? currentTextPanel?.bgColor : currentTextPanel?.top?.backgroundColor
                      )}
                      onChoose={value => handleUpdateValues('backgroundColor', value, PANEL_NAME.TOP)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                    />
                  </div>
                  <div className={styles.fontColorPickerContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.border_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(currentTextPanel?.top?.borderColor)}
                      onChoose={value => handleUpdateValues('borderColor', value, PANEL_NAME.TOP)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                      parentRef={infoRef}
                    />
                  </div>
                </div>
                <div className={styles.bordersContainer}>
                  <div className={styles.borderContainer}>
                    <div className={styles.borderInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.text_panels.editor.border_radius')}, <span>px</span>
                      </span>
                      <input
                        type="text"
                        className={cx(styles.inputField, styles.largeInputField)}
                        value={currentTextPanel?.top?.borderRadius}
                        onChange={event => handleUpdateValues('borderRadius', event.target?.value, PANEL_NAME.TOP)}
                      />
                    </div>
                    <Slider
                      min={BORDER_RADIUS_MIN}
                      max={BORDER_RADIUS_MAX}
                      step={1}
                      onChange={value => handleUpdateValues('borderRadius', value, PANEL_NAME.TOP)}
                      value={currentTextPanel?.top?.borderRadius}
                      included
                      style={{ left: '-6px', width: '50%' }}
                      handleStyle={handleStyle}
                      trackStyle={trackStyle}
                      railStyle={railStyle}
                      marks={{
                        [BORDER_RADIUS_MIN]: ' ',
                        [BORDER_RADIUS_MAX]: ' ',
                      }}
                      dotStyle={dotStyle}
                    />
                  </div>
                  <div className={styles.borderContainer}>
                    <div className={styles.borderInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.text_panels.editor.border_width')}, <span>px</span>
                      </span>
                      <input
                        type="text"
                        className={cx(styles.inputField, styles.largeInputField)}
                        value={currentTextPanel?.top?.borderWidth}
                        onChange={event => handleUpdateValues('borderWidth', event.target?.value, PANEL_NAME.TOP)}
                      />
                    </div>
                    <Slider
                      min={BORDER_WIDTH_MIN}
                      max={BORDER_WIDTH_MAX}
                      step={1}
                      onChange={value => handleUpdateValues('borderWidth', value, PANEL_NAME.TOP)}
                      value={currentTextPanel?.top?.BORDER_WIDTH_MIN}
                      included
                      style={{ left: '-6px', width: '50%' }}
                      handleStyle={handleStyle}
                      trackStyle={trackStyle}
                      railStyle={railStyle}
                      marks={{
                        [BORDER_WIDTH_MIN]: ' ',
                        [BORDER_WIDTH_MAX]: ' ',
                      }}
                      dotStyle={dotStyle}
                    />
                  </div>
                </div>
                <div className={styles.paddingsContainer}>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_left')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[3]
                          : currentTextPanel?.top?.textPadding?.split(' ')[3]
                      }
                      onChange={event => handleUpdateValues('paddingLeft', event.target?.value, PANEL_NAME.TOP)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_right')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[1]
                          : currentTextPanel?.top?.textPadding?.split(' ')[1]
                      }
                      onChange={event => handleUpdateValues('paddingRight', event.target?.value, PANEL_NAME.TOP)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_top')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[0]
                          : currentTextPanel?.top?.textPadding?.split(' ')[0]
                      }
                      onChange={event => handleUpdateValues('paddingTop', event.target?.value, PANEL_NAME.TOP)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_bottom')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[2]
                          : currentTextPanel?.top?.textPadding?.split(' ')[2]
                      }
                      onChange={event => handleUpdateValues('paddingBottom', event.target?.value, PANEL_NAME.TOP)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.panelSection}>
              <span className={styles.panelTitle}>{t('company.settings.text_panels.editor.bottom_panel')}</span>
              <div className={styles.panelValuesContainer}>
                <div className={styles.fontSizeContainer}>
                  <div className={styles.fontSelectContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font')}</span>
                    <SelectOption
                      currentlySelected={availableFonts.find(({ id }) => id === currentTextPanel?.bottom?.font?.id)}
                      options={availableFonts}
                      onSelect={value => handleUpdateValues('font', value, PANEL_NAME.BOTTOM)}
                      currentInputClassName={styles.currentSelect}
                    />
                  </div>
                  <div className={styles.fontSizeInputContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font_size')}</span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={currentTextPanel?.bottom?.fontSize}
                      onChange={event => handleUpdateValues('fontSize', event.target?.value, PANEL_NAME.BOTTOM)}
                      onBlur={() => currentTextPanel?.bottom?.fontSize === '' && handleUpdateValues('fontSize', 1, PANEL_NAME.BOTTOM)}
                    />
                  </div>
                </div>
                <div className={styles.fontColorContainer}>
                  <div className={styles.fontColorPickerContainer}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.font_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(currentTextPanel?.bottom?.fontColor)}
                      onChoose={value => handleUpdateValues('fontColor', value, PANEL_NAME.BOTTOM)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                    />
                  </div>
                  <div className={cx(styles.fontColorPickerContainer, { [styles.unavailableOption]: isOnePanelType })}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.background_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(
                        isOnePanelType ? currentTextPanel?.bgColor : currentTextPanel?.bottom?.backgroundColor
                      )}
                      onChoose={value => handleUpdateValues('backgroundColor', value, PANEL_NAME.BOTTOM)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                    />
                  </div>
                  <div className={cx(styles.fontColorPickerContainer, { [styles.unavailableOption]: isOnePanelType })}>
                    <span className={styles.inputText}>{t('company.settings.text_panels.editor.border_color')}</span>
                    <ColorPicker
                      currentColor={convertColorIfNeeded(currentTextPanel?.bottom?.borderColor)}
                      onChoose={value => handleUpdateValues('borderColor', value, PANEL_NAME.BOTTOM)}
                      className={styles.colorPickerContainer}
                      colorImageClassName={styles.colorImageContainer}
                      isShowCurrentColor={true}
                        parentRef={infoRef}
                    />
                  </div>
                </div>
                <div className={cx(styles.bordersContainer, { [styles.unavailableOption]: isOnePanelType })}>
                  <div className={styles.borderContainer}>
                    <div className={styles.borderInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.text_panels.editor.border_radius')}, <span>px</span>
                      </span>
                      <input
                        type="text"
                        className={cx(styles.inputField, styles.largeInputField)}
                        value={currentTextPanel?.bottom?.borderRadius ?? 0}
                        onChange={event => handleUpdateValues('borderRadius', event.target?.value, PANEL_NAME.BOTTOM)}
                      />
                    </div>
                    <Slider
                      min={BORDER_RADIUS_MIN}
                      max={BORDER_RADIUS_MAX}
                      disabled={currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type}
                      step={1}
                      onChange={value => handleUpdateValues('borderRadius', value, PANEL_NAME.BOTTOM)}
                      value={currentTextPanel?.bottom?.borderRadius ?? 0}
                      included
                      style={{ left: '-6px', width: '50%', background: 'none' }}
                      handleStyle={{
                        ...handleStyle,
                        ...(currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type ? { background: '#f0f0f0' } : {}),
                      }}
                      trackStyle={trackStyle}
                      railStyle={railStyle}
                      marks={{
                        [BORDER_RADIUS_MIN]: ' ',
                        [BORDER_RADIUS_MAX]: ' ',
                      }}
                      dotStyle={dotStyle}
                    />
                  </div>
                  <div className={styles.borderContainer}>
                    <div className={styles.borderInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.text_panels.editor.border_width')}, <span>px</span>
                      </span>
                      <input
                        type="text"
                        className={cx(styles.inputField, styles.largeInputField)}
                        value={currentTextPanel?.bottom?.borderWidth ?? 0}
                        onChange={event => handleUpdateValues('borderWidth', event.target?.value, PANEL_NAME.BOTTOM)}
                      />
                    </div>
                    <Slider
                      min={BORDER_WIDTH_MIN}
                      max={BORDER_WIDTH_MAX}
                      step={1}
                      disabled={currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type}
                      onChange={value => handleUpdateValues('borderWidth', value, PANEL_NAME.BOTTOM)}
                      value={currentTextPanel?.bottom?.borderWidth ?? 0}
                      included
                      style={{ left: '-6px', width: '50%', background: 'none' }}
                      handleStyle={{
                        ...handleStyle,
                        ...(currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type ? { background: '#f0f0f0' } : {}),
                      }}
                      trackStyle={trackStyle}
                      railStyle={railStyle}
                      marks={{
                        [BORDER_WIDTH_MIN]: ' ',
                        [BORDER_WIDTH_MAX]: ' ',
                      }}
                      dotStyle={dotStyle}
                    />
                  </div>
                </div>
                <div className={cx(styles.paddingsContainer, { [styles.unavailableOption]: isOnePanelType })}>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_left')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[3]
                          : currentTextPanel?.bottom?.textPadding?.split(' ')[3]
                      }
                      onChange={event => handleUpdateValues('paddingLeft', event.target?.value, PANEL_NAME.BOTTOM)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_right')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[1]
                          : currentTextPanel?.bottom?.textPadding?.split(' ')[1]
                      }
                      onChange={event => handleUpdateValues('paddingRight', event.target?.value, PANEL_NAME.BOTTOM)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_top')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[0]
                          : currentTextPanel?.bottom?.textPadding?.split(' ')[0]
                      }
                      onChange={event => handleUpdateValues('paddingTop', event.target?.value, PANEL_NAME.BOTTOM)}
                    />
                  </div>
                  <div className={styles.paddingContainer}>
                    <span className={styles.paddingInputText}>
                      {t('company.settings.text_panels.editor.padding_bottom')}, <span>px</span>
                    </span>
                    <input
                      type="text"
                      className={cx(styles.inputField, styles.largeInputField)}
                      value={
                        currentTextPanel?.type === PANEL_TYPE.ONE_PANEL.type
                          ? currentTextPanel?.textPadding?.split(' ')[2]
                          : currentTextPanel?.bottom?.textPadding?.split(' ')[2]
                      }
                      onChange={event => handleUpdateValues('paddingBottom', event.target?.value, PANEL_NAME.BOTTOM)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.footerContainer}>
              <CustomButtonTooltip
                variant="primary"
                label={t('general.save')}
                className={styles.footerBtn}
                onClick={handleSave}
                disabled={!isChanged || noChosenFont}
                loadingSpinner={true}
                loading={isLoading}
                tooltip={noChosenFont ? t('company.settings.text_panels.editor.no_fonts_tooltip') : ''}
                tooltipClassName={styles.saveButtonTooltip}
              />
              <CustomButton
                variant="secondary"
                label={t('general.cancel')}
                className={styles.footerBtn}
                onClick={handleCancel}
                disabled={!isChanged || isLoading}
              />
            </div>
          </div>
          <div ref={previewRef} className={styles.previewContainer}>
            <span className={styles.previewTitle}>{t('company.settings.text_panels.editor.preview')}</span>
              <div ref={handleLandscapeNodeRef} id={PREVIEW_IDS.landscape} className={styles.landscapePreview}>
              {!landscapeLoading && (
                <div style={{ ...landscapePosition, ...landscapeTextPanelStyles?.containerStyles, position: 'absolute' }}>
                  <div style={landscapeTextPanelStyles.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                  <div
                    style={{
                      ...landscapeTextPanelStyles.bottomLabelStyles,
                      width: currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type ? '100%' : 'fit-content',
                    }}
                  >
                    {EXAMPLE_BOTTOM_TEXT}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.squarePortraitContainer}>
                <div ref={handlePortraitNodeRef} id={PREVIEW_IDS.portrait} className={styles.portraitPreview}>
                {!portraitLoading && (
                  <div style={{ ...portraitPosition, ...portraitTextPanelStyles?.containerStyles, position: 'absolute' }}>
                    <div style={portraitTextPanelStyles.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                    <div
                      style={{
                        ...portraitTextPanelStyles.bottomLabelStyles,
                        width: currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type ? '100%' : 'fit-content',
                      }}
                    >
                      {EXAMPLE_BOTTOM_TEXT}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.squarePreviewContainer}>
                  <div ref={handleSquareNodeRef} id={PREVIEW_IDS.square} className={styles.squarePreview}>
                  {!squareLoading && (
                    <div style={{ ...squarePosition, ...squareTextPanelStyles?.containerStyles, position: 'absolute' }}>
                      <div style={squareTextPanelStyles.topLabelStyles}>{EXAMPLE_TOP_TEXT}</div>
                      <div
                        style={{
                          ...squareTextPanelStyles.bottomLabelStyles,
                          width: currentTextPanel.type === PANEL_TYPE.ONE_PANEL.type ? '100%' : 'fit-content',
                        }}
                      >
                        {EXAMPLE_BOTTOM_TEXT}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TextPanelManagementEditor;
